import { Check, Close } from "@mui/icons-material";
import { Autocomplete, Box, Button, FormHelperText, TextField, Typography } from "@mui/material";
import Modal from "../../../App/Components/Modal/Modal";
import { AppFeatureLookup } from "../../../App/Constants/AbacConstants";
import { useFeaturePermission } from "../../../App/State/Hooks/abacHooks";
import { useNotificationStore } from "../../../App/State/Stores/NotificationStore";
import { useManageCandidateSelectionContext } from "../../State/ManageCandidateContext";
import { useAddToGroupState } from "./State/AddtoGroupState";

export function AddToGroupOptions() {
  const addToGroupButtonAccess = useFeaturePermission(AppFeatureLookup.CandidatesAddCandidatesToGroup);
  const {
    getters: { isAddToGroupOpen, selectedCandidates },
    setters: { setAddToGroupOpen },
  } = useManageCandidateSelectionContext();
  const state = useAddToGroupState();
  const {
    getters: { autoCompleteFilter, groupDropdownList = [] },
    handlers: { handleGroupSelect, handleGroupSave, handleCancelAddToGroup },
  } = state;
  const snackbarSeverity = useNotificationStore((state) => state.type);

  return (
    <Modal
      title="Add Candidates To Group"
      dataTestId="add-candidates"
      isOpen={addToGroupButtonAccess && isAddToGroupOpen}
      handleCloseModal={() => setAddToGroupOpen(false)}
      content={
        <>
          <Box>
            <Autocomplete
              data-test="addtogroup-autocomplete"
              options={groupDropdownList}
              selectOnFocus
              clearOnBlur
              onChange={handleGroupSelect}
              filterOptions={(options, params) => {
                const filtered = autoCompleteFilter(options, params);

                const { inputValue } = params;

                // Suggest the creation of a new value
                const isExisting = options.some((option) => inputValue === option.name);
                if (inputValue !== "" && !isExisting) {
                  filtered.push({
                    id: "",
                    name: `Add "${inputValue}"`,
                  });
                }

                return filtered;
              }}
              handleHomeEndKeys
              getOptionLabel={(group) => {
                if (typeof group === "string") {
                  return group;
                } else {
                  return group.name;
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select a Group"
                  error={snackbarSeverity === "error" || snackbarSeverity === "warning"}
                  color={
                    snackbarSeverity === "error"
                      ? "error"
                      : snackbarSeverity === "warning"
                        ? "warning"
                        : "primary"
                  }
                  data-test="addtogroup-searchfield"
                />
              )}
              sx={{ width: 1 }}
            />
            <FormHelperText>Select candidates from the list using the checkboxes</FormHelperText>
          </Box>
          <Box sx={{ my: 1 }}>
            <Typography>
              {`${selectedCandidates.length} ${
                selectedCandidates.length === 1 ? "candidate" : "candidates"
              } selected`}
            </Typography>
          </Box>
        </>
      }
      footerContent={
        <Box sx={{ display: "flex", gap: "2ch", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            data-test={`addtogroup-save`}
            onClick={handleGroupSave}
            size="small"
            color="primary"
            aria-label="Submit candidates to group"
            title="Submit candidates to group"
            startIcon={<Check fontSize="small" />}
          >
            Add to Group
          </Button>
          <Button
            variant="outlined"
            data-test={`addtogroup-cancel`}
            onClick={handleCancelAddToGroup}
            size="small"
            color="warning"
            aria-label="Cancel adding candidates to group"
            title="Cancel adding candidates to group"
            startIcon={<Close fontSize="small" />}
          >
            Cancel
          </Button>
        </Box>
      }
    />
  );
}
